import React, { useState, useEffect, useRef } from 'react';
import { useAppContext } from '@/context/AppContext';
import { Language } from '@/types';
import { SocialsData } from '@/data/socials-data';
import { TiArrowDownThick } from 'react-icons/ti';

import styles from './Home.module.scss';
import ImageComponent from '@/components/imageComponent/ImageComponent';
import LoadingSpinner from '@/components/loadingspinner/LoadingSpinner';
import SiteHeader from '@/components/siteHeader/SiteHeader';

const Home: React.FC = () => {
  const {
    language,
    setLanguage,
    isChangingLanguage,
    previousLanguage,
    setIsChangingLanguage,
    setPreviousLanguage,
    scrollDirection,
  } = useAppContext();

  const isProjectsDeepLink =
    typeof window !== 'undefined' && window.location.hash === '#projects';
  const [isInitialLoading, setIsInitialLoading] = useState(!isProjectsDeepLink);
  const [showLoadingSpinner, setShowLoadingSpinner] =
    useState(!isProjectsDeepLink);
  const [showHeader, setShowHeader] = useState(isProjectsDeepLink);
  const [onLoad, setOnLoad] = useState(false);
  const [animationStep, setAnimationStep] = useState(0);
  const [showScrollArrow, setShowScrollArrow] = useState(false);
  const [isArrowFading, setIsArrowFading] = useState(false);
  const [hasScrolledOnce, setHasScrolledOnce] = useState(false);

  useEffect(() => {
    if (isInitialLoading) {
      const loadingTimer = setTimeout(() => {
        setShowLoadingSpinner(false);
        setTimeout(() => setShowHeader(true), 120);
        setTimeout(() => setIsInitialLoading(false), 300);
      }, 700);

      return () => clearTimeout(loadingTimer);
    } else {
      setShowLoadingSpinner(false);
      setShowHeader(true);
    }
  }, [isInitialLoading]);

  useEffect(() => {
    if (isProjectsDeepLink) {
      setShowLoadingSpinner(false);
      setShowHeader(true);
      setOnLoad(true);
      setAnimationStep(4);
      setShowScrollArrow(false);
      return;
    }
    if (!isInitialLoading && !onLoad) {
      setIsChangingLanguage(false);
      setPreviousLanguage(language);
      setHasScrolledOnce(false);

      const timer = setTimeout(() => {
        setOnLoad(true);
        setTimeout(() => setAnimationStep(1), 120);
        setTimeout(() => setAnimationStep(2), 500);
        setTimeout(() => setAnimationStep(3), 900);
        setTimeout(() => setAnimationStep(4), 1700);
        setTimeout(() => setShowScrollArrow(true), 2200);
      }, 360);

      return () => clearTimeout(timer);
    }
  }, [
    isInitialLoading,
    onLoad,
    language,
    setIsChangingLanguage,
    setPreviousLanguage,
    isProjectsDeepLink,
  ]);

  useEffect(() => {
    if (language !== previousLanguage && onLoad) {
      setIsChangingLanguage(true);
      const timer = setTimeout(() => {
        setPreviousLanguage(language);
        setIsChangingLanguage(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [
    language,
    onLoad,
    previousLanguage,
    setIsChangingLanguage,
    setPreviousLanguage,
  ]);

  useEffect(() => {
    if (isProjectsDeepLink) {
      setShowScrollArrow(false);
      return;
    }
    if (
      showScrollArrow &&
      !isArrowFading &&
      !hasScrolledOnce &&
      scrollDirection === 'down'
    ) {
      setIsArrowFading(true);
      setHasScrolledOnce(true);
      setTimeout(() => {
        setShowScrollArrow(false);
        setIsArrowFading(false);
      }, 500);
    }
  }, [
    showScrollArrow,
    isArrowFading,
    hasScrolledOnce,
    scrollDirection,
    isProjectsDeepLink,
  ]);

  return (
    <>
      {showLoadingSpinner && <LoadingSpinner />}

      <div className={styles.home} id="home">
        <SiteHeader show={showHeader} animated logoHref="#home" />

        <div className={styles.container}>
          <div className={styles.left} />
          <div className={styles.right}>
            <div className={styles.greeting}>
              <div
                className={`${styles.wrapper} ${onLoad ? styles.show : styles.hide}`}
              >
                {onLoad && (
                  <>
                    <h2
                      className={`${animationStep >= 1 ? styles.show : styles.hide} ${isChangingLanguage ? styles.changingLanguage : ''}`}
                    >
                      {previousLanguage === Language.EN
                        ? 'Hi there!'
                        : 'Olá! Sou o'}
                    </h2>
                    <h1
                      className={`${animationStep >= 2 ? styles.show : styles.hide} ${isChangingLanguage ? styles.changingLanguage : ''}`}
                    >
                      {previousLanguage === Language.EN ? (
                        <>
                          <span className={styles.thinText}>I'm </span>
                          <span>João Lemos</span>
                        </>
                      ) : (
                        <>
                          <span className={styles.thinText}></span>
                          <span>João Lemos</span>
                        </>
                      )}
                    </h1>
                    <h3
                      className={`${animationStep >= 3 ? styles.show : styles.hide} ${isChangingLanguage ? styles.changingLanguage : ''}`}
                    >
                      <span
                        className={
                          previousLanguage === Language.PT ? styles.noWrap : ''
                        }
                      >
                        {previousLanguage === Language.PT
                          ? 'Desenvolvedor Front-end'
                          : 'Front-end Developer'}
                      </span>
                    </h3>
                    {onLoad && (
                      <div
                        className={`${styles.socialIcons} ${animationStep >= 4 ? styles.show : styles.hide}`}
                      >
                        {Object.values(SocialsData).map((social, index) => (
                          <a
                            key={social.name}
                            href={social.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`${styles.socialIcon} ${animationStep >= 4 ? styles.show : ''}`}
                            style={{ animationDelay: `${index * 0.2}s` }}
                          >
                            <ImageComponent
                              name={social.name}
                              img={social.icon}
                              desc={social.name}
                            />
                          </a>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            <button
              className={`${styles.scrollArrow} ${
                showScrollArrow ? styles.showOnLoad : ''
              } ${isArrowFading ? styles.fading : ''}`}
              onClick={() => {
                const aboutSection = document.querySelector('#about') as HTMLElement;
                const scrollContainer = document.querySelector('#scrollRoot') as HTMLElement;
                
                if (aboutSection && scrollContainer) {
                  const targetTop = aboutSection.offsetTop;
                  const startTop = scrollContainer.scrollTop;
                  const distance = targetTop - startTop;
                  const duration = 800;
                  let startTime: number | null = null;

                  const smoothScroll = (currentTime: number) => {
                    if (startTime === null) startTime = currentTime;
                    const timeElapsed = currentTime - startTime;
                    const progress = Math.min(timeElapsed / duration, 1);
                    const easeInOutCubic = (t: number) => t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
                    const currentTop = startTop + distance * easeInOutCubic(progress);
                    
                    scrollContainer.scrollTop = currentTop;
                    
                    if (progress < 1) {
                      requestAnimationFrame(smoothScroll);
                    }
                  };

                  requestAnimationFrame(smoothScroll);
                }
              }}
            >
              <TiArrowDownThick />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Home;
